import { ReactNode, useRef } from 'react';
import TabsProvider, { useTabsContext } from './TabsContext';
import clsx from 'clsx';

type Props = {
  children: ReactNode;
  className?: string;
  reRender?: boolean;
  defaultIndex?: number;
  value?: number;
  onChange?: (index: number) => void;
};

const Tabs = ({ children, className, reRender = true, defaultIndex, value, onChange }: Props) => {
  return (
    <TabsProvider reRender={reRender} defaultIndex={defaultIndex} value={value} onChange={onChange}>
      {children}
    </TabsProvider>
  );
};

const TabsMenuGroup = ({ children, className = 'justify-start' }: Props) => {
  const ulRef = useRef<HTMLUListElement | null>(null);

  return (
    <div className="px-1 max-sm:px-0 overflow-x-auto">
      <ul
        ref={ulRef}
        role="tablist"
        className={clsx(
          'flex w-full whitespace-nowrap overflow-x-auto no-scrollbar border-b border-gray-200 dark:border-slate-700',
          className,
        )}
      >
        {children}
      </ul>
    </div>
  );
};

//@ts-ignore
TabsMenuGroup.displayName = 'Tabs.MenuGroup';
Tabs.MenuGroup = TabsMenuGroup;

interface ButtonProps {
  index: number;
  title: string;
  className?: string;
  onChange?: () => void;
  children?: ReactNode;
}

const TabsMenu = ({ index, title, className, onChange, children }: ButtonProps) => {
  const { currentIndex, setCurrentIndex } = useTabsContext();

  const onChangeMenu = (index: number, e: React.MouseEvent) => {
    if (onChange) onChange();
    setCurrentIndex(index);

    // Auto scroll into view when clicked
    const target = e.currentTarget as HTMLElement;
    target.scrollIntoView({
      behavior: 'smooth',
      inline: 'center',
      block: 'nearest',
    });
  };

  const isActive = currentIndex === index;

  return (
    <li
      role="tab"
      aria-selected={isActive}
      onClick={(e) => onChangeMenu(index, e)}
      className={clsx(
        `cursor-pointer relative px-4 py-2 text-sm font-medium transition-colors duration-200`,
        isActive
          ? 'text-brand-600 dark:text-brand-400 border-b-2 border-brand-600 dark:border-brand-400'
          : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-white',
        className,
      )}
    >
      {children}
      {title}
    </li>
  );
};

//@ts-ignore
TabsMenu.displayName = 'Tabs.Menu';
Tabs.Menu = TabsMenu;

const TabsPanelGroup = ({ children, className }: Props) => {
  return (
    <div className={clsx('px-2 max-sm:px-0', className)}>
      <div className="mt-4 h-full flex flex-col">{children}</div>
    </div>
  );
};

//@ts-ignore
TabsPanelGroup.displayName = 'Tabs.PanelGroup';
Tabs.PanelGroup = TabsPanelGroup;

interface PanelProps {
  index: number;
  children: ReactNode;
  className?: string;
}

const TabsPanel = ({ index, children, className }: PanelProps) => {
  const { currentIndex, reRender } = useTabsContext();

  if (!reRender) {
    return (
      <div
        role="tabpanel"
        hidden={currentIndex !== index}
        className={clsx(currentIndex !== index ? 'hidden' : className || 'block')}
      >
        {children}
      </div>
    );
  }

  if (currentIndex === index) {
    return (
      <div role="tabpanel" className={clsx(className || 'block')}>
        {children}
      </div>
    );
  }

  return null;
};

//@ts-ignore
TabsPanel.displayName = 'Tabs.Panel';
Tabs.Panel = TabsPanel;

export default Tabs;
